/**
 * RBAC-3.6 · Count, preview and save a role's permissions.
 *
 * ⚠️ THIS MODULE HOLDS NO RULES AND READS NO TABLE. Who is counted, what the diff is and when a save
 * is refused are all `20261101b7`; a `count(*)` from here would be whatever RLS lets the caller see.
 * What the answers mean is `lib/rolePermissionDiff.ts`.
 */

import { supabase } from '@/lib/supabaseClient';
import type { RpcErrorLike } from '@/lib/roleDuplication';
import {
  ROLE_HOLDER_COUNT_RPC,
  ROLE_SAVE_PERMISSIONS_RPC,
  ROLE_SAVE_PREVIEW_RPC,
  parseRoleHolders,
  parseRoleSaveDiff,
  roleSaveErrorMessage,
  saveRoleArgs,
  type RoleHolders,
  type RoleSaveDiff,
} from '@/lib/rolePermissionDiff';

export class RoleSaveError extends Error {
  readonly code: string | null;
  constructor(err: RpcErrorLike | null) {
    super(roleSaveErrorMessage(err));
    this.name = 'RoleSaveError';
    this.code = err?.code ?? null;
  }
}

const unreadable = (what: string) =>
  new RoleSaveError({ message: `${what} came back in a shape this screen does not understand. Reload to see the current state.` });

export async function fetchRoleHolders(roleId: number): Promise<RoleHolders> {
  const { data, error } = await supabase.rpc(ROLE_HOLDER_COUNT_RPC, { p_role_id: roleId });
  if (error) throw new RoleSaveError(error);
  const h = parseRoleHolders(data);
  if (!h) throw unreadable('The count of people on this role');
  return h;
}

export async function previewRoleSave(roleId: number, permissions: readonly string[]): Promise<RoleSaveDiff> {
  const { data, error } = await supabase.rpc(ROLE_SAVE_PREVIEW_RPC, {
    p_role_id: roleId,
    p_permission_names: [...new Set(permissions)].sort(),
  });
  if (error) throw new RoleSaveError(error);
  const d = parseRoleSaveDiff(data);
  if (!d) throw unreadable('The list of changes');
  return d;
}

/** `reviewed` must be the preview the admin confirmed: its count is the number on the button. */
export async function saveRolePermissions(
  roleId: number,
  permissions: readonly string[],
  base: readonly string[],
  reviewed: RoleSaveDiff,
): Promise<RoleSaveDiff> {
  const { data, error } = await supabase.rpc(ROLE_SAVE_PERMISSIONS_RPC, saveRoleArgs(roleId, permissions, base, reviewed));
  if (error) throw new RoleSaveError(error);
  const d = parseRoleSaveDiff(data);
  if (!d) throw unreadable('The saved role');
  return d;
}
